import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Camera, LineChart, Users, ShieldCheck, Loader2, Send } from "lucide-react";
import { buildHead, PAGE_SEO, pickLang } from "@/lib/seo";

export const Route = createFileRoute("/_public/vende-tu-propiedad")({
  head: ({ match }) => {
    const lang = pickLang(match.search);
    const copy = PAGE_SEO["vender"]![lang];
    // El cuerpo de esta landing solo existe en español: no declaramos alternates
    // para evitar discrepancias de hreflang.
    return buildHead({ lang, path: "/vende-tu-propiedad", ...copy, alternates: false });
  },
  component: SellPage,
});


const BENEFITS = [
  {
    icon: LineChart,
    title: "Tasación profesional gratuita",
    desc: "Análisis de mercado y valor real basado en operaciones recientes en tu zona.",
  },
  {
    icon: Camera,
    title: "Fotos y publicación destacada",
    desc: "Fotografía profesional y difusión en nuestro portal y redes sociales.",
  },
  {
    icon: Users,
    title: "Base de compradores activos",
    desc: "Cartera cualificada de personas buscando en Asunción y Central.",
  },
  {
    icon: ShieldCheck,
    title: "Acompañamiento legal",
    desc: "Te guiamos en toda la documentación hasta la firma final.",
  },
];

function SellPage() {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [details, setDetails] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || (!phone.trim() && !email.trim())) {
      toast.error("Ingresá tu nombre y al menos un contacto.");
      return;
    }
    setSending(true);
    try {
      const notes = [address && `Ubicación: ${address}`, details && `Detalles: ${details}`]
        .filter(Boolean)
        .join("\n");
      const { data: contact, error: cErr } = await supabase
        .from("contacts")
        .insert({
          full_name: name.trim(),
          email: email.trim() || null,
          phone: phone.trim() || null,
          source: "web_vende",
          notes: notes || null,
        })
        .select("id")
        .single();
      if (cErr) throw cErr;

      const { error: lErr } = await supabase.from("leads").insert({
        contact_id: contact.id,
        channel: "web",
        pipeline: "venta",
        stage: "nuevo",
        message: notes || null,
      });
      if (lErr) throw lErr;

      toast.success("¡Solicitud recibida! Te contactaremos a la brevedad.");
      setName("");
      setPhone("");
      setEmail("");
      setAddress("");
      setDetails("");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "No se pudo enviar");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden bg-primary text-primary-foreground">
        <div className="absolute inset-0 opacity-[0.06] [background-image:radial-gradient(circle_at_1px_1px,white_1px,transparent_0)] [background-size:24px_24px]" />
        <div className="relative mx-auto grid max-w-6xl gap-10 px-4 py-16 md:grid-cols-[1.2fr_1fr] md:items-start md:px-6 md:py-20">
          <div>
            <p className="mb-4 inline-flex items-center gap-2 rounded-full border border-accent/40 bg-white/5 px-3 py-1 text-xs font-medium uppercase tracking-wider text-accent">
              Vendé con confianza
            </p>
            <h1 className="font-serif text-4xl leading-tight md:text-6xl">
              Vendé tu propiedad al precio justo, en menos tiempo.
            </h1>
            <p className="mt-5 max-w-xl text-base text-primary-foreground/85 md:text-lg">
              Tasación gratuita, fotos profesionales y una estrategia de difusión pensada para
              atraer compradores reales.
            </p>
            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              {BENEFITS.map((b) => (
                <div key={b.title} className="rounded-xl border border-white/10 bg-white/5 p-4">
                  <b.icon className="h-5 w-5 text-accent" />
                  <div className="mt-2 text-sm font-semibold">{b.title}</div>
                  <p className="mt-1 text-xs text-primary-foreground/70">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Form */}
          <form
            onSubmit={submit}
            className="rounded-2xl bg-card p-6 text-foreground shadow-card md:p-8"
          >
            <h2 className="font-serif text-2xl">Solicitá tu tasación gratuita</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Completá el formulario y te contactamos en menos de 24hs.
            </p>
            <div className="mt-5 space-y-3">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Nombre completo"
                aria-label="Nombre completo"
                className={inputCls}
                required
              />
              <input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="WhatsApp / Teléfono"
                aria-label="WhatsApp o teléfono"
                className={inputCls}
              />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                aria-label="Email"
                className={inputCls}
              />
              <input
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="Ubicación de la propiedad (barrio, ciudad)"
                aria-label="Ubicación de la propiedad"
                className={inputCls}
              />
              <textarea
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                rows={4}
                aria-label="Detalles de la propiedad"
                placeholder="Contanos brevemente sobre la propiedad (tipo, m², dormitorios)"
                className={`${inputCls} resize-none`}
              />
              <button
                type="submit"
                disabled={sending}
                className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-accent px-4 py-3 text-sm font-semibold text-accent-foreground shadow-soft transition hover:brightness-95 disabled:opacity-60"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Solicitar tasación gratuita
              </button>
              <p className="text-center text-xs text-muted-foreground">
                Tus datos están protegidos. Solo los usamos para contactarte.
              </p>
            </div>
          </form>
        </div>
      </section>

      {/* Proceso */}
      <section className="mx-auto max-w-6xl px-4 py-16 md:px-6">
        <h2 className="font-serif text-3xl text-foreground md:text-4xl">Nuestro proceso</h2>
        <div className="mt-8 grid gap-6 md:grid-cols-4">
          {[
            { n: "01", t: "Contacto inicial", d: "Analizamos tus objetivos y expectativas." },
            { n: "02", t: "Tasación", d: "Valuación basada en el mercado real." },
            { n: "03", t: "Publicación", d: "Fotos profesionales y difusión estratégica." },
            { n: "04", t: "Cierre", d: "Negociación y acompañamiento legal completo." },
          ].map((s) => (
            <div key={s.n} className="rounded-xl border border-border bg-card p-6">
              <div className="font-serif text-3xl text-accent">{s.n}</div>
              <div className="mt-2 font-semibold text-foreground">{s.t}</div>
              <p className="mt-1 text-sm text-muted-foreground">{s.d}</p>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}

const inputCls =
  "w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring";
